import { useEffect, useRef, useState } from 'react';
import type { ICountry } from 'rn-international-phone-number';
import { getCountryByPhoneNumber } from 'rn-international-phone-number';
import {
  detectCountry,
  type CountryDetectionResult,
} from '../utils/getdefaultCountry';
import { DEFAULT_COUNTRY_CODE, DEFAULT_CALLING_CODE } from '../CONSTANTS';
import {
  currencyForCountryCode,
  normalizeCurrencyCode,
} from '../utils/currency.ts';

export type UseDetectedCountryResult = {
  isLoading: boolean;
  error: string | null;
  result: CountryDetectionResult | null;
  countryCode: string; // CCA2 e.g., "KE"
  callingCode: string; // e.g., "254" (no leading +)
  countryName: string;
  currency: string; // ISO 4217 e.g., "KES"
  iCountry: ICountry | undefined;
  refresh: () => void;
};

const DEFAULTS: CountryDetectionResult = {
  countryCode: DEFAULT_COUNTRY_CODE,
  country: 'Kenya',
  callingCode: String(DEFAULT_CALLING_CODE),
  currency: 'KES',
};

export function useDetectedCountry(): UseDetectedCountryResult {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CountryDetectionResult | null>(null);
  const [iCountry, setICountry] = useState<ICountry | undefined>(undefined);
  const refreshRef = useRef(0);

  const performDetect = async (signal: { cancelled: boolean }) => {
    setIsLoading(true);
    setError(null);
    try {
      const detected = await detectCountry();
      console.log(detected, 'detected');
      if (signal.cancelled) return;
      const finalDetected = detected || DEFAULTS;
      // Currency must come from react-native-country-picker-modal data (no guessing).
      const resolvedCurrency = await currencyForCountryCode(
        finalDetected.countryCode,
        finalDetected.currency || DEFAULTS.currency,
      );
      setResult({ ...finalDetected, currency: resolvedCurrency });
      // Try to resolve ICountry using calling code (works for defaulting)
      const cc = String(
        finalDetected.callingCode || DEFAULTS.callingCode,
      ).replace(/^\+/, '');
      const guess = getCountryByPhoneNumber(`+${cc}`) as unknown as
        | ICountry
        | undefined;
      setICountry(guess);
    } catch (e: any) {
      if (signal.cancelled) return;
      setResult(DEFAULTS);
      try {
        const guess = getCountryByPhoneNumber(
          `+${DEFAULTS.callingCode}`,
        ) as unknown as ICountry | undefined;
        setICountry(guess);
      } catch {
        setICountry(undefined);
      }
      setError(e?.message || 'Failed to detect country');
    } finally {
      if (!signal.cancelled) setIsLoading(false);
    }
  };

  useEffect(() => {
    const state = { cancelled: false };
    performDetect(state);
    return () => {
      state.cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshRef.current]);

  const refresh = () => {
    refreshRef.current += 1;
  };

  const countryCode =
    (result && result.countryCode) ||
    (DEFAULTS && DEFAULTS.countryCode) ||
    'KE';
  const callingCode =
    (result && result.callingCode) ||
    (DEFAULTS && DEFAULTS.callingCode) ||
    '254';
  const countryName =
    (result && result.country) || (DEFAULTS && DEFAULTS.country) || 'Kenya';
  const currency = normalizeCurrencyCode(result?.currency, DEFAULTS.currency);

  return {
    isLoading,
    error,
    result,
    countryCode,
    callingCode,
    countryName,
    currency,
    iCountry,
    refresh,
  };
}
